import { SettingOutlined, SettingFilled, UserOutlined, ContainerOutlined, ContainerFilled } from '@ant-design/icons';
import ProLayout from '@ant-design/pro-layout';
import { Grid, Menu } from 'antd';
import { Content } from 'antd/es/layout/layout';
import React, { useState } from 'react';
import { Link, Route, Routes, useResolvedPath, useLocation } from 'react-router-dom';
import { RequestListPage } from '../cases';
import { Settings } from './settings';
import { Profile } from './profile';

interface HomeProps {}

export const Cases: React.FC<HomeProps> = () => {
  const casesRoutePath = useResolvedPath('cases');
  const settingsRoutePath = useResolvedPath('settings');
  const profileRoutePath = useResolvedPath('profile');
  const location = useLocation();

  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [clickedId, setClickedId] = useState<string | null>(null);
  const screen = Grid.useBreakpoint();
  const isMobile = screen.xs;

  const MenuContent: React.FC<{ matchedIds: string[]; pageLayouts: any[] }> = ({ matchedIds, pageLayouts }) => {
    const createMenuItems = () => {
      return pageLayouts.map((layout) => {
        const isHovered = hoveredId === layout.id;
        const isClicked = clickedId === layout.id;
        const isSelected = matchedIds.includes(layout.id);
        const icon =
          isHovered || isClicked || isSelected
            ? React.cloneElement(layout.filledIcon, { style: { color: '#3f4373', fontSize: '24px' } })
            : React.cloneElement(layout.icon, { style: { fontSize: '24px' } });

        return {
          key: layout.id,
          label: (
            <Link to={layout.path}>
              <div>{icon}</div>
              <div>{layout.title}</div>
            </Link>
          ),
          style: {
            paddingTop: '16px',
            paddingBottom: '16px',
            paddingLeft: '24px',
            paddingRight: '24px',
            minHeight: '70px',
            minWidth: '55px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            lineHeight: '1.2',
            borderRadius: '0',
            backgroundColor: isSelected ? '#d3d6f8' : 'transparent',
            fontSize: '10px',
            width: '100%',
            marginInline: '0px',
            marginBlock: '0px'
          },
          onMouseEnter: () => setHoveredId(layout.id),
          onMouseLeave: () => setHoveredId(null),
          onClick: () => {
            setClickedId(layout.id);
            if (isMobile) {
              setCollapsed(true);
            }
          }
        };
      });
    };

    return (
      <Menu
        defaultSelectedKeys={['1']}
        mode="inline"
        style={{
          width: '100%'
        }}
        theme="light"
        selectedKeys={matchedIds}
        items={createMenuItems() as any}
      ></Menu>
    );
  };

  const pageLayouts = [
    {
      path: `${casesRoutePath.pathname}`,
      title: 'Cases',
      icon: <ContainerOutlined />,
      filledIcon: <ContainerFilled />,
      id: '1'
    },
    {
      path: `${settingsRoutePath.pathname}`,
      title: 'Settings',
      icon: <SettingOutlined />,
      filledIcon: <SettingFilled />,
      id: '2',
      parent: 'ROOT'
    },
    {
      path: `${profileRoutePath.pathname}`,
      title: 'Profile',
      icon: <UserOutlined />,
      filledIcon: <UserOutlined />,
      id: '3',
      parent: 'ROOT'
    }
  ];

  const matchPartialRoute = (currentPath: string, routePath: string) => {
    return currentPath.startsWith(routePath);
  };

  const getMatchedPageIds = (pageLayouts: any[], location: { pathname: string }) => {
    const currentPath = location.pathname;

    return pageLayouts.filter((layout) => matchPartialRoute(currentPath, layout.path)).map((layout) => layout.id.toString());
  };

  const matchedIds = getMatchedPageIds(pageLayouts, location);

  const [collapsed, setCollapsed] = useState(true);

  return (
    <ProLayout
      token={{
        sider: {
          paddingInlineLayoutMenu: 0
        }
      }}
      menuContentRender={() => <MenuContent matchedIds={matchedIds} pageLayouts={pageLayouts} />}
      menuHeaderRender={() => null}
      footerRender={() => null}
      collapsedButtonRender={() => null}
      onCollapse={() => setCollapsed(!collapsed)}
      collapsed={collapsed}
      siderWidth={86}
      contentStyle={{
        paddingBlock: '0px',
        paddingInline: '0px',
        height: '100%',
        width: '100%',
        backgroundColor: '#ffffff',
        zIndex: 1
      }}
    >
      <Content style={{ height: 'calc(100% - 55px)', overflowY: isMobile ? 'unset' : 'auto' }}>
        <Routes>
          <Route path={'cases/*'} element={<RequestListPage />} />
          <Route path={'settings/*'} element={<Settings />} />
          <Route path={'profile/*'} element={<Profile />} />
        </Routes>
      </Content>
    </ProLayout>
  );
};
